import React, { useState } from 'react';

import { ExpandLess, ExpandMore } from '@mui/icons-material';
import { FormControlLabel, Radio, RadioGroup, FormLabel, styled, ListItemButton, Checkbox } from '@mui/material';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useContainer } from 'unstated-next';

import Instructors from './Instructors';
import { Collapsible } from '../../../../../components/common/collapsible';
import { Error } from '../../../../../components/forms/styles';
import ClassContainer from '../../ClassContainer';
import TemplateOptions from '../../TemplateOptions';
import { CanJoinBeforeInstructor, RequireAuth, WaitingRoom } from '../../types';
import { StyledFieldset, StyledFieldsetAdvanced } from '../index.styles';

const ExpandMoreButton = styled(ListItemButton)`
    cursor: pointer;
    display: flex;
    justify-content: flex-start;
    font-size: 1rem;

    padding: 1rem 0.5rem;
    margin: 0 0.75rem;

    border: 0;
    outline: 0;

    .MuiButtonBase-root:hover {
        background-color: transparent;
    }
`;

const SectionTitle = styled('div')`
    font-weight: 600;
    margin-left: 0.5rem;
`;

const Advanced: React.FC = () => {
    const { t } = useTranslation('calendar');
    const { control, publishedTemplates, formActionType, allowForcedAuthentication, ltiState } =
        useContainer(ClassContainer);

    const [expanded, setExpanded] = useState(false);

    const handleClick = () => {
        setExpanded((previous) => !previous);
    };

    const showTemplateOption = publishedTemplates.length > 0 && formActionType === 'CREATE';
    const showContentTypeOption = ltiState && Object.keys(ltiState).length !== 0;

    return (
        <>
            <ExpandMoreButton aria-controls="advanced-section" onClick={handleClick}>
                {expanded ? <ExpandLess /> : <ExpandMore />}
                <SectionTitle>{t('advanced-settings')}</SectionTitle>
            </ExpandMoreButton>
            <Collapsible id="advanced-section" expanded={expanded}>
                {showTemplateOption ? (
                    <StyledFieldset>
                        <Controller
                            name="courseTemplate"
                            control={control}
                            render={(props) => (
                                <>
                                    <FormControlLabel
                                        label={t('use-a-course-template')}
                                        control={<Checkbox {...props.field} />}
                                    />
                                    {props?.fieldState?.error ? <Error>{props.fieldState.error.message}</Error> : null}
                                </>
                            )}
                        />
                        <TemplateOptions />
                    </StyledFieldset>
                ) : null}
                <StyledFieldsetAdvanced>
                    {showContentTypeOption ? (
                        <Controller
                            control={control}
                            name="contentType"
                            render={({ field }) => (
                                <div>
                                    <FormLabel id="content-type-toggle">{t('content-type')}</FormLabel>
                                    <RadioGroup aria-labelledby="content-type-toggle" {...field} row>
                                        <FormControlLabel
                                            value={'native'}
                                            control={<Radio />}
                                            label={t('content-type-class')}
                                        />
                                        <FormControlLabel
                                            value={'lms'}
                                            control={<Radio />}
                                            label={t('content-type-lms')}
                                        />
                                    </RadioGroup>
                                </div>
                            )}
                        />
                    ) : null}
                    <Controller
                        control={control}
                        name="waitingRoom"
                        render={({ field }) => (
                            <div>
                                <FormLabel id="waiting-room-toggle">{t('waiting-room')}</FormLabel>
                                <RadioGroup aria-labelledby="waiting-room-toggle" {...field} row>
                                    <FormControlLabel value={WaitingRoom.ON} control={<Radio />} label={t('on')} />
                                    <FormControlLabel value={WaitingRoom.OFF} control={<Radio />} label={t('off')} />
                                </RadioGroup>
                            </div>
                        )}
                    />
                    <Controller
                        control={control}
                        name="canJoinBeforeInstructor"
                        render={({ field }) => (
                            <div>
                                <FormLabel id="can-join-before-instructor">
                                    {t('students-can-join-before-instructor')}
                                </FormLabel>
                                <RadioGroup aria-labelledby="can-join-before-instructor" {...field} row>
                                    <FormControlLabel
                                        value={CanJoinBeforeInstructor.YES}
                                        control={<Radio />}
                                        label={t('yes')}
                                    />
                                    <FormControlLabel
                                        value={CanJoinBeforeInstructor.NO}
                                        control={<Radio />}
                                        label={t('no')}
                                    />
                                </RadioGroup>
                            </div>
                        )}
                    />
                    {allowForcedAuthentication ? (
                        <Controller
                            control={control}
                            name="requireAuth"
                            render={({ field }) => (
                                <div>
                                    <FormLabel id="require-auth">{t('require-authentication')}</FormLabel>
                                    <RadioGroup aria-labelledby="require-auth" {...field} row>
                                        <FormControlLabel value={RequireAuth.ON} control={<Radio />} label={t('on')} />
                                        <FormControlLabel
                                            value={RequireAuth.OFF}
                                            control={<Radio />}
                                            label={t('off')}
                                        />
                                    </RadioGroup>
                                </div>
                            )}
                        />
                    ) : null}
                </StyledFieldsetAdvanced>
                <Instructors />
            </Collapsible>
        </>
    );
};

export default Advanced;
